import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import "./App.css";
import { checkUser } from "./api/auth";
import LandingPage from "./components/Landing/Index";
import { EmployeeProps } from "./interfaces/employee_type";
import { LoadingComponent } from "./Loading";
import { AppRoutes } from "./Routes";

// Main Router for the application
export default function App() {
	return <AppRoutes />;
}

export interface AuthContextType {
	auth: boolean;
	user: EmployeeProps | null;
	loading: boolean;
	setAuth: (auth: boolean) => void;
	setUser: (user: EmployeeProps | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
	undefined,
);

/**
 * Get the current auth context
 *
 * Use this to see through a user's data
 *
 * @returns the current auth context
 */
export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

/**
 * Bridge Component for login verification
 *
 * Check if user is authenticated,
 *
 * if so, go to where the user wants to go
 *
 * if not, redirect to login.
 *
 * #### This component triggers EVERY time the user navigates anywhere in it's children.
 *
 * @param isAuth: boolean
 * @param redirectPath: string
 */
export function Verification() {
	const [auth, isAuth] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [user, setUser] = useState<EmployeeProps | null>(null);
	const location = useLocation();

	useEffect(() => {
		// Check if user is logged in
		const checkForValidation = async () => {
			setLoading(true);

			try {
				const user = await checkUser();
				setUser(user);
				isAuth(true);
			} catch (error) {
				console.error("Error checking user:", error);
				isAuth(false);
				setUser(null);
			} finally {
				setLoading(false);
			}
		};

		checkForValidation();
	}, [location]);

	// Change this to a custom loading screen
	if (loading) {
		return (
			<AuthContext.Provider
				value={{ auth, user, loading, setAuth: isAuth, setUser }}
			>
				<LoadingComponent />
			</AuthContext.Provider>
		);
	}

	if (!auth) {
		return <Navigate to="/" />;
	}

	return (
		<AuthContext.Provider
			value={{ auth, user, loading, setAuth: isAuth, setUser }}
		>
			{auth ? <Outlet /> : <LandingPage />}
		</AuthContext.Provider>
	);
}
